import { Component, Input, OnInit } from '@angular/core';
import { WebService, lastValueFrom, NavTabService, RmsPulldownService } from '@services';
import { RmsModule } from '@rms';
import { Column, ComputedColumn, HeaderButton } from '@interfaces';
import { OutboundDataService } from '../../../../services/outbound-data.service';
import { OutboundOrderDetailComponent } from './outbound-order-detail/outbound-order-detail.component';
import { OutboundReconciliationComponent } from './outbound-reconciliation/outbound-reconciliation.component';
// 5.2.3: same "My Orders" screen used in Order Management — imported as a
// plain TS class only, opened dynamically, not templated here.
import { MyOrdersStatusComponent } from '../order-management/my-orders-status/my-orders-status.component';
import { take } from 'rxjs/operators';
import { OrderDocumentPreviewService } from '../../../../services/order-document-preview.service';

interface OutboundOrder {
  OrderID: number;
  Customer: string;
  WarehouseNumber: number | null;
  Warehouse: string | null;
  PickupDate: string | null;
  ProposedPickupDate: string | null;
  TruckingRate: string | null;
  TruckingQuote: string | null;
  DOFlag: boolean;
  BOLFlag: boolean;
  // NEW: real Ready/Sent/Pending text per document, replacing the raw
  // true/false the DOFlag/BOLFlag booleans were showing in the table.
  DOStatus: string;
  BOLStatus: string;
  // NEW (5.2.26): plain-language next step, matches the wireframe's
  // "Next Action" column exactly.
  NextAction: string;
  OrderStatusID: number;
  StatusName: string;
  Trucker: number | null;
  TruckerName: string | null;
  HoldReason: string | null;
  Bags: number;
  Selected?: boolean;
  ActiveBatchID?: number;
}

// NEW (5.2.25): the two fields custom grouping can nest by, in order.
// 'none' at either level just skips that level of nesting.
type GroupField = 'none' | 'PickupDate' | 'Warehouse';

interface GroupedBucket {
  label: string;
  subgroups: { label: string; orders: OutboundOrder[] }[];
}

@Component({
  selector: 'app-outbound-management',
  imports: [RmsModule],
  templateUrl: './outbound-management.component.html',
  styleUrl: './outbound-management.component.scss'
})
export class OutboundManagementComponent implements OnInit {
  @Input() embedded = false;
  IncludeCompleted = false;
  ActiveFilter: 'all' | 'open' | 'attention' | 'ready' | 'warehouse' = 'all';
  private filteredSource?: OutboundOrder[];
  private filteredKey = '';
  private filteredCutoffs?: Set<string>;
  private filteredRows: OutboundOrder[] = [];

  setFilter(filter: typeof this.ActiveFilter) {
    this.ActiveFilter = this.ActiveFilter === filter ? 'all' : filter;
    this.clearSelection();
  }

  private needsAttention(o: OutboundOrder): boolean {
    return o.OrderStatusID === 8 || (o.OrderStatusID === 5 && !!o.PickupDate &&
      this.PastCutoffKeys.has(`${o.WarehouseNumber}|${o.PickupDate.split('T')[0]}`) && !(o.DOFlag && o.BOLFlag));
  }

  get visibleOrders(): OutboundOrder[] {
    if (this.filteredSource !== this.OutboundList || this.filteredKey !== this.ActiveFilter || this.filteredCutoffs !== this.PastCutoffKeys) {
      this.filteredSource = this.OutboundList;
      this.filteredKey = this.ActiveFilter;
      this.filteredCutoffs = this.PastCutoffKeys;
      this.filteredRows = this.OutboundList.filter(o => {
        switch (this.ActiveFilter) {
          case 'open': return [5, 8].includes(o.OrderStatusID);
          case 'attention': return this.needsAttention(o);
          case 'ready': return o.OrderStatusID === 5 && o.DOStatus === 'Ready' && o.BOLStatus === 'Ready';
          case 'warehouse': return o.OrderStatusID === 6;
          default: return true;
        }
      });
    }
    return this.filteredRows;
  }
  CutoffWarning = "";
  private refreshVersion = 0;
  OutboundList: OutboundOrder[] = [];
  OutboundColumns: Column[] = [];
  CreatingBatch = false;
  Loading = false;
  ActionError = '';
  BatchSuccess = '';
  ShowBatches = false;
  LoadingBatches = false;
  BatchError = '';
  Batches: any[] = [];
  PreviewingBatch = false;

  private PastCutoffKeys = new Set<string>();

  // 5.2.3: "My Orders" sits here, next to Reports.
  Buttons: HeaderButton[] = [
    {
      label: 'My Orders',
      onAction: () => this.openMyOrdersStatus()
    },
    {
      label: 'Reports', menu: [
        { label: 'Report Based On Current View', onAction: () => { console.log('Report'); } },
      ]
    }
  ];

  // 5.2.25: custom grouping controls. Default matches the pain-point
  // example directly: "Group by pick up date then group by warehouse".
  PrimaryGroupField: GroupField = 'none';
  SecondaryGroupField: GroupField = 'none';
  GroupFieldOptions: { label: string, value: GroupField }[] = [
    { label: 'None', value: 'none' },
    { label: 'Pickup Date', value: 'PickupDate' },
    { label: 'Warehouse', value: 'Warehouse' },
  ];

  get isGrouped(): boolean {
    return this.PrimaryGroupField !== 'none';
  }

  // Builds the nested grouping structure fresh from the current
  // OutboundList/PrimaryGroupField/SecondaryGroupField whenever the
  // template reads it — cheap enough given typical outbound list sizes,
  // and avoids a separate cache to keep in sync on every list refresh.
  get groupedOrders(): GroupedBucket[] {
    if (!this.isGrouped) return [];

    const primaryKeyOf = (o: OutboundOrder) => this.groupKeyValue(o, this.PrimaryGroupField);
    const secondaryKeyOf = (o: OutboundOrder) => this.groupKeyValue(o, this.SecondaryGroupField);

    const primaryMap = new Map<string, OutboundOrder[]>();
    for (const order of this.visibleOrders) {
      const key = primaryKeyOf(order);
      if (!primaryMap.has(key)) primaryMap.set(key, []);
      primaryMap.get(key)!.push(order);
    }

    const buckets: GroupedBucket[] = [];
    for (const [primaryLabel, ordersInGroup] of primaryMap) {
      if (this.SecondaryGroupField === 'none') {
        buckets.push({ label: primaryLabel, subgroups: [{ label: '', orders: ordersInGroup }] });
        continue;
      }

      const secondaryMap = new Map<string, OutboundOrder[]>();
      for (const order of ordersInGroup) {
        const key = secondaryKeyOf(order);
        if (!secondaryMap.has(key)) secondaryMap.set(key, []);
        secondaryMap.get(key)!.push(order);
      }

      buckets.push({
        label: primaryLabel,
        subgroups: Array.from(secondaryMap.entries()).map(([label, orders]) => ({ label, orders }))
      });
    }

    // Sort groups so pickup-date grouping reads chronologically rather than
    // in whatever order rows happened to appear in the list
    return buckets.sort((a, b) => a.label.localeCompare(b.label));
  }

  private groupKeyValue(order: OutboundOrder, field: GroupField): string {
    if (field === 'PickupDate') return order.PickupDate ? order.PickupDate.split('T')[0] : 'No Pickup Date';
    if (field === 'Warehouse') return order.Warehouse ?? 'No Warehouse';
    return '';
  }

  get openOrdersCount() { return this.OutboundList.filter(o => [5, 8].includes(o.OrderStatusID)).length; }

  get needAttentionCount() {
    return this.OutboundList.filter(o => {
      if (o.OrderStatusID === 8) return true;
      if (!o.PickupDate || !o.WarehouseNumber) return false;

      const key = `${o.WarehouseNumber}|${o.PickupDate.split('T')[0]}`;
      const pastCutoff = this.PastCutoffKeys.has(key);
      return o.OrderStatusID === 5 && pastCutoff && !(o.DOFlag && o.BOLFlag);
    }).length;
  }

  // Still keys off the raw DOFlag/BOLFlag booleans (unchanged, still
  // returned by the proc) — only the DISPLAYED columns switched to
  // DOStatus/BOLStatus text; these getters don't need to change.
  get readyToSendCount() {
    return this.OutboundList.filter(o => o.OrderStatusID === 5 && o.DOStatus === 'Ready' && o.BOLStatus === 'Ready').length;
  }
  get sentTodayCount() {
    return this.OutboundList.filter(o => o.OrderStatusID === 6).length;
  }

  get selectedOrders(): OutboundOrder[] {
    return this.OutboundList.filter(o => o.Selected);
  }

  get canCreateBatch(): boolean {
    return !this.batchSelectionIssue && !this.CreatingBatch && !this.Loading;
  }

  get batchSelectionIssue(): string {
    const orders = this.selectedOrders;
    if (!orders.length) return 'Select orders for one warehouse and one confirmed pickup date.';
    if (orders.length > 500) return 'Select up to 500 orders per batch.';
    if (orders.some(o => o.ActiveBatchID)) return 'Some selected orders already belong to an active batch.';
    if (orders.some(o => o.OrderStatusID !== 5)) return 'Only orders sent to outbound can be batched.';
    if (!orders[0].WarehouseNumber || orders.some(o => o.WarehouseNumber !== orders[0].WarehouseNumber)) return 'Select orders from the same warehouse.';
    if (orders.some(o => !o.PickupDate)) return 'Confirm a pickup date for every selected order.';
    if (orders.some(o => o.PickupDate!.split('T')[0] !== orders[0].PickupDate!.split('T')[0])) return 'Select orders with the same confirmed pickup date.';
    return '';
  }

  clearSelection() { this.OutboundList.forEach(o => o.Selected = false); }

  async loadBatches() {
    this.ShowBatches = true;
    this.LoadingBatches = true;
    this.BatchError = '';
    try {
      const batches = await lastValueFrom(this.outboundData.getWarehouseBatches());
      if (batches.some(b => !Array.isArray(b.Orders))) {
        this.Batches = [];
        this.BatchError = 'Batch details require the updated API. Restart the Node API, then refresh batches.';
      } else { this.Batches = batches; }
    }
    catch { this.BatchError = 'Could not load warehouse batches. Please retry.'; }
    finally { this.LoadingBatches = false; }
  }

  async previewBatch(batch: any, type: 'Delivery Order' | 'Bill of Lading') {
    if (this.PreviewingBatch) return;
    this.PreviewingBatch = true;
    this.BatchError = '';
    try { await this.documentPreview.viewBatch(batch.Orders.map((o: any) => o.OrderID), type, batch.BatchID); }
    catch { this.BatchError = 'Could not prepare the complete batch preview. No documents were sent. Please retry.'; }
    finally { this.PreviewingBatch = false; }
  }

  constructor(
    private webService: WebService,
    private outboundData: OutboundDataService,
    private navTab: NavTabService,
    private pulldownService: RmsPulldownService,
    private documentPreview: OrderDocumentPreviewService
  ) { }

  async ngOnInit() {
    this.OutboundColumns = this.CreateOutboundColumns();
    await this.refresh();
  }

  async refresh() {
    if (this.Loading) return;
    this.Loading = true;
    const version = ++this.refreshVersion;
    this.ActionError = '';
    this.CutoffWarning = '';
    this.PastCutoffKeys.clear();
    try {
      this.OutboundList = await this.GetOutboundList();
    } catch {
      this.ActionError = 'Could not refresh shipping orders. Please retry.';
    } finally { this.Loading = false; }
    await this.loadPastCutoffKeys(version).catch(() => { if(version !== this.refreshVersion) return; this.CutoffWarning = "Warehouse cutoff information is unavailable. Refresh to retry."; });
  }

  async GetOutboundList(): Promise<OutboundOrder[]> {
    const list = await lastValueFrom(this.outboundData.getOutboundOrders({ IncludeHistory: this.IncludeCompleted }));
    return list.map((o: OutboundOrder) => ({ ...o, Selected: false,
      NextAction: o.OrderStatusID === 7 ? 'Complete' : o.OrderStatusID === 6 ? 'Awaiting Warehouse' : o.NextAction?.replace('Generate DO', 'View delivery order').replace('Generate BOL', 'View bill of lading') }));
  }

  async loadPastCutoffKeys(version = this.refreshVersion) {
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    const capacity = await lastValueFrom(this.outboundData.getCapacityStatus(today, today, true));
    if(version !== this.refreshVersion) return;
    this.PastCutoffKeys = new Set(
      capacity
        .filter((c: any) => c.TodayPastCutoff)
        .map((c: any) => `${c.WarehouseID}|${c.PickupDate.split('T')[0]}`)
    );
    this.OutboundList = this.OutboundList.map(order => {
      const key = `${order.WarehouseNumber}|${order.PickupDate?.split('T')[0]}`;
      const nextAction = order.NextAction?.replace(/^Past warehouse cutoff · /, '') ?? 'Review';
      return { ...order, NextAction: order.OrderStatusID === 5 && !(order.DOFlag && order.BOLFlag) && this.PastCutoffKeys.has(key)
        ? `Past warehouse cutoff · ${nextAction}` : nextAction };
    });
  }

  // DOFlag/BOLFlag columns replaced with DOStatus/BOLStatus (real
  // Ready/Sent/Pending text instead of raw true/false), and NextAction
  // added as its own column, matching the wireframe.
  CreateOutboundColumns(): Column[] {
    return [
      { key: 'Selected', label: '', type: 'checkbox', hidden: false, width: 4, edit: true },
      { key: 'OrderID', label: 'Order #', type: 'number', hidden: false, clickable: true, onActionKey: 'openOutboundDetail', onAction: (row) => this.openOutboundDetail(row) },
      { key: 'Customer', label: 'Customer', hidden: false },
      { key: 'TraderName', label: 'Trader', hidden: false },
      { key: 'Bags', label: 'Bags', type: 'number', hidden: false },
      { key: 'ActiveBatchID', label: 'Batch #', type: 'number', hidden: false },
      { key: 'Warehouse', label: 'Warehouse', hidden: false },
      { key: 'PickupDate', label: 'Pickup', type: 'date', SearchType: 'date', hidden: false },
      { key: 'TruckingRate', label: 'Rate', hidden: false },
      { key: 'DOStatus', label: 'DO', hidden: false, type: 'select', SearchType: 'select' },
      { key: 'BOLStatus', label: 'BOL', hidden: false, type: 'select', SearchType: 'select' },
      { key: 'StatusName', label: 'Status', hidden: false, type: 'select', SearchType: 'select' },
      { key: 'NextAction', label: 'Next Action', hidden: false },
      { key: 'TruckerName', label: 'Carrier', hidden: true },
      { key: 'HoldReason', label: 'Hold Reason', hidden: true },
    ];
  }

  openOutboundDetail(order: OutboundOrder) {
    const ref = this.pulldownService.open(OutboundOrderDetailComponent, {
      data: { OrderID: order.OrderID },
      title: `Order #${order.OrderID} · Shipping`,
      width: '80vw',
      maxWidth: '95vw',
      top: '0',
      maxHeight: '92vh',
      minHeight: '60vh'
    });

    ref.afterClosed().pipe(take(1)).subscribe({
      next: async () => {
        await this.refresh();
      }
    });
  }

  openReconciliation() {
    this.pulldownService.open(OutboundReconciliationComponent, {
      title: 'Outbound Reconciliation',
      width: '80vw',
      maxWidth: '95vw',
      top: '0',
      maxHeight: '92vh',
      minHeight: '60vh'
    });
  }

  // 5.2.3: opens the "My Orders" screen — same shared component as Order
  // Management, showing a trader's own orders with outbound status.
  // Read-only, so no refresh-on-close needed here.
  openMyOrdersStatus() {
    this.pulldownService.open(MyOrdersStatusComponent, {
      title: 'My Orders',
      width: '70vw',
      maxWidth: '90vw',
      top: '0',
      maxHeight: '90vh',
      minHeight: '50vh'
    });
  }

  async createBatch() {
    if (!this.canCreateBatch) return;
    this.CreatingBatch = true;
    this.ActionError = '';
    this.BatchSuccess = '';
    try {
      const warehouseId = this.selectedOrders[0].WarehouseNumber!;
      const orderIds = this.selectedOrders.map(o => o.OrderID);
      const result = await lastValueFrom(this.outboundData.createWarehouseBatch(warehouseId, orderIds));
      this.OutboundList.forEach(o => {
        if (orderIds.includes(o.OrderID)) { o.ActiveBatchID = result.BatchID; o.Selected = false; }
      });
      this.BatchSuccess = `Batch #${result.BatchID} created with ${orderIds.length} order(s). Documents have not been sent.`;
      await this.loadBatches();
    } catch (error: any) {
      this.ActionError = error?.error?.error ?? 'Could not create the warehouse batch. Please retry.';
    } finally {
      this.CreatingBatch = false;
    }
  }
}
